import {
  DiscountForm,
  DiscountAmoutType,
  DiscountType,
  Province,
  DiscountApplyForType,
} from "@/types";
import { ROUTE } from "@/types/router";
import { IconButton, Theme, useMediaQuery } from "@mui/material";
import { useFormik } from "formik";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import toast from "react-hot-toast";

import dayjs from "dayjs";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import EditDiscountForm from "@/components/editDiscount/EditDiscountForm";
import {
  fetchDiscountDetailService,
  updateDiscountService,
} from "@/services/discount.service";
import { DateRange } from "react-day-picker";
import { QUERY_KEY } from "@/constants";

const initialValues: DiscountForm = {
  discountName: "",
  discountCode: "",
  discountValue: 0,
  discountType: DiscountType.PERCENTAGE,
  startDate: "",
  endDate: "",
  active: true,
  discountCities: [],
};

const validationSchema = Yup.object().shape({
  discountName: Yup.string().required("Discount name is required"),
  discountCode: Yup.string().required("Discount code is required"),
  usageLimit: Yup.number()
    .typeError("")
    .min(1, "Usage limit must be greater than 0")
    .optional()
    .nullable(),
  usageLimitPerUser: Yup.number()
    .typeError("")
    .min(1, "Usage per user must be greater than 0")
    .optional()
    .nullable(),
  minimumSpend: Yup.number()
    .typeError("")
    .min(0, "Minimum spend must be greater than or equal to 0")
    .optional()
    .nullable(),
  startDate: Yup.string().required("Valid date is required"),
  endDate: Yup.string().required("Valid date is required"),
  discountValue: Yup.number()
    .typeError("")
    .min(0, "Discount amount must be greater than 0")
    .required("Discount amount is required"),
  maximumDiscountValue: Yup.number()
    .typeError("")
    .min(0, "Maximum discount must be greater than or equal to 0.02")
    .optional()
    .nullable(),
  discountType: Yup.string().required("Discount type is required"),
  discountCities: Yup.array().optional(),
});

const EditDiscountPage = () => {
  const navigation = useNavigate();
  const { id } = useParams();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [selectedDiscountType, setSelectedDiscountType] =
    useState<DiscountAmoutType>(DiscountAmoutType.PERCENTAGE);
  const [isOpen, setIsOpen] = useState(false);
  const refForm = useRef<DiscountForm>(initialValues);
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm"),
  );
  const [countryCode, setCountryCode] = useState<string>("");
  const [cities, setCities] = useState<string[]>([]);
  const [cityData, setCityData] = useState<{ provinces: Province[] } | null>(
    null,
  );
  const queryClient = useQueryClient();

  useEffect(() => {
    document.body.scrollTop = 0; // For Safari
    document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
  }, []);

  const { data: dataDiscount } = useQuery({
    queryKey: [QUERY_KEY.fetchDiscountDetail, id],
    queryFn: () => fetchDiscountDetailService(id || ""),
    enabled: !!id,
  });

  const handleUpdateDiscount = useCallback(
    async (formData: DiscountForm) => {
      try {
        setLoading(true);

        // Validate city selection
        if (countryCode && cities.length === 0) {
          toast.error("Please select at least one city");
          return;
        }

        const discountCities = cities.map((cityCode) => {
          const city = cityData?.provinces
            .flatMap((province: Province) => province.citys)
            .find((c) => c.cityCode === cityCode);
          const country = cityData?.provinces.find((province) =>
            province.citys.some((c) => c.cityCode === cityCode),
          );
          return {
            cityCode: city?.cityCode || "",
            cityName: city?.cityName || "",
            countryCode: countryCode,
            countryName: country?.countryName || "",
          };
        });

        const formDataToSubmit = {
          ...formData,
          discountType:
            formData.discountType === DiscountType.PERCENTAGE
              ? DiscountType.PERCENTAGE
              : DiscountType.FLAT,
          startDate: dayjs(formData.startDate).toISOString(),
          endDate: dayjs(
            new Date(formData.endDate).setHours(23, 59, 59, 999),
          ).toISOString(),
          discountCities,
          usageLimit: !formData.usageLimit ? null : formData.usageLimit,
          usageLimitPerUser: !formData.usageLimitPerUser
            ? null
            : formData.usageLimitPerUser,
          minimumSpend: !formData.minimumSpend ? null : formData.minimumSpend,
          maximumDiscountValue: !formData.maximumDiscountValue
            ? null
            : formData.maximumDiscountValue,
          discountApplyFor: DiscountApplyForType.HOTEL,
        };

        await updateDiscountService(formDataToSubmit, id || "");
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY.fetchDiscountDetail, id],
        });
        toast.success("Discount successfully updated");
        navigation(ROUTE.DiscountManagement);
      } catch (error: unknown) {
        if (
          error &&
          typeof error === "object" &&
          "status" in error &&
          error.status === 406
        ) {
          toast.error("Discount code already exists");
        } else {
          toast.error("Update discount failed!");
        }
      } finally {
        setLoading(false);
      }
    },
    [navigation, id, cities, cityData, countryCode],
  );

  const {
    handleChange: formikHandleChange,
    submitForm,
    errors,
    values,
    setFieldValue,
    validateField,
    setValues,
  } = useFormik<DiscountForm>({
    initialValues,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: handleUpdateDiscount,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (
      [
        "usageLimit",
        "usageLimitPerUser",
        "maximumDiscountValue",
        "discountValue",
        "minimumSpend",
      ].includes(name)
    ) {
      setFieldValue(name, value === "" ? "" : Number(value));
    } else {
      formikHandleChange(e);
    }
  };

  const handleDiscountTypeChange = (type: DiscountAmoutType) => {
    setSelectedDiscountType(type);
    setFieldValue(
      "discountType",
      type === DiscountAmoutType.PERCENTAGE
        ? DiscountType.PERCENTAGE
        : DiscountType.FLAT,
    );
    if (type === DiscountAmoutType.FIXED_AMOUNT) {
      setFieldValue("maximumDiscountValue", "");
    }
  };

  const handleDateChange = (date: DateRange | undefined) => {
    setFieldValue("startDate", dayjs(date?.from).toISOString());
    setFieldValue("endDate", dayjs(date?.to).toISOString());
    setValues({
      ...values,
      startDate: dayjs(date?.from).toISOString(),
      endDate: dayjs(date?.to).toISOString(),
    });
  };

  const handleChangeCountry = (code: string) => {
    setCountryCode(code);
    setCities([]); // Reset cities when country changes
  };

  const handleChangeCity = (cityCode: string) => {
    setCities((prevValues) => {
      if (prevValues.includes(cityCode)) {
        return prevValues.filter((code) => code !== cityCode);
      } else {
        return [...prevValues, cityCode];
      }
    });
  };

  useEffect(() => {
    if (dataDiscount) {
      setValues({
        ...dataDiscount,
      });
      setSelectedDiscountType(
        dataDiscount.discountType === DiscountType.PERCENTAGE
          ? DiscountAmoutType.PERCENTAGE
          : DiscountAmoutType.FIXED_AMOUNT,
      );

      if (
        dataDiscount.discountCities &&
        dataDiscount.discountCities.length > 0
      ) {
        const firstCity = dataDiscount.discountCities[0];
        setCountryCode(firstCity.countryCode);
        setCities(dataDiscount.discountCities.map((city) => city.cityCode));
      }
    }
  }, [dataDiscount]);

  useEffect(() => {
    if (refForm.current.discountName !== values.discountName) {
      validateField("discountName");
    }
    if (refForm.current.discountCode !== values.discountCode) {
      validateField("discountCode");
    }
    if (refForm.current.discountType !== values.discountType) {
      validateField("discountType");
    }
    if (refForm.current.discountValue !== values.discountValue) {
      validateField("discountValue");
    }
    if (refForm.current.startDate !== values.startDate) {
      validateField("startDate");
    }
    if (refForm.current.endDate !== values.endDate) {
      validateField("endDate");
    }
    if (refForm.current.usageLimit !== values.usageLimit) {
      validateField("usageLimit");
    }
    if (refForm.current.usageLimitPerUser !== values.usageLimitPerUser) {
      validateField("usageLimitPerUser");
    }
    if (refForm.current.minimumSpend !== values.minimumSpend) {
      validateField("minimumSpend");
    }
    if (refForm.current.maximumDiscountValue !== values.maximumDiscountValue) {
      validateField("maximumDiscountValue");
    }

    refForm.current = values;
  }, [values, validateField]);

  return (
    <>
      <div className="-mt-16 mb-[14px] flex w-1/2 items-center gap-4 max-sm:mt-5">
        <IconButton
          aria-label="back"
          size="medium"
          sx={{
            border: "1px solid #E2E8F0",
            borderRadius: "8px",
          }}
          className="size-10 max-sm:size-8"
          onClick={() => navigation(ROUTE.DiscountManagement)}
        >
          <img src="/icons/arrow-left.svg" alt="arrow-left" />
        </IconButton>

        <h2 className="text-3xl font-semibold text-text max-sm:text-lg">
          Edit Discount
        </h2>
      </div>

      <EditDiscountForm
        values={values}
        errors={errors}
        handleChange={handleChange}
        selectedDiscountType={selectedDiscountType}
        handleDiscountTypeChange={handleDiscountTypeChange}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        isMobile={isMobile}
        isLoading={isLoading}
        submitForm={submitForm}
        onCancel={() => navigation(ROUTE.DiscountManagement)}
        handleDateChange={handleDateChange}
        countryCode={countryCode}
        onCountryChange={handleChangeCountry}
        cities={cities}
        onCityChange={handleChangeCity}
        onCityDataChange={setCityData}
      />
    </>
  );
};

export default EditDiscountPage;
